import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Pagination,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import "./Home.css";
import { dashboard } from "../../config/images";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts/highstock";
import {
  dashboardCounts,
  jobProviderList,
  jobProviderUpdate,
  userGrowthGraph,
  getJobProviderPerformance,
  adminDashboardReportExport,
} from "../Dashboard/services";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { downloadBinaryFile } from "../../utils/utils";

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
  transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 12px 28px rgba(0,0,0,0.15)",
  },
  padding: "20px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  width: "100%",
}));

// This component styles the icon container.
const IconWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  padding: theme.spacing(2),
  width: 64,
  height: 64,
}));

// --- SVG Icons ---
const UsersIcon = () => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient id="usersGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" style={{ stopColor: "#a855f7", stopOpacity: 1 }} />
        <stop offset="100%" style={{ stopColor: "#6366f1", stopOpacity: 1 }} />
      </linearGradient>
    </defs>
    <path
      d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"
      fill="url(#usersGradient)"
    />
    <circle cx="8.5" cy="7" r="4" fill="url(#usersGradient)" />
    <path
      d="M20 8v6m3-3h-6"
      stroke="url(#usersGradient)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const JobsIcon = () => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient id="jobsGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" style={{ stopColor: "#ec4899", stopOpacity: 1 }} />
        <stop offset="100%" style={{ stopColor: "#8b5cf6", stopOpacity: 1 }} />
      </linearGradient>
    </defs>
    <rect
      x="2"
      y="7"
      width="20"
      height="14"
      rx="2"
      ry="2"
      fill="url(#jobsGradient)"
    />
    <path
      d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"
      fill="url(#jobsGradient)"
      opacity="0.6"
    />
  </svg>
);

const RevenueIcon = () => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient id="revenueGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" style={{ stopColor: "#22c55e", stopOpacity: 1 }} />
        <stop offset="100%" style={{ stopColor: "#3b82f6", stopOpacity: 1 }} />
      </linearGradient>
    </defs>
    <line
      x1="12"
      y1="1"
      x2="12"
      y2="23"
      stroke="url(#revenueGradient)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"
      stroke="url(#revenueGradient)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const HealthIcon = () => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient id="healthGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" style={{ stopColor: "#f43f5e", stopOpacity: 1 }} />
        <stop offset="100%" style={{ stopColor: "#60a5fa", stopOpacity: 1 }} />
      </linearGradient>
    </defs>
    <rect
      x="2"
      y="3"
      width="20"
      height="14"
      rx="2"
      ry="2"
      fill="url(#healthGradient)"
    />
    <path
      d="M8 21h8"
      stroke="url(#healthGradient)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <line
      x1="12"
      y1="17"
      x2="12"
      y2="21"
      stroke="url(#healthGradient)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Home = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [apiData, setApiData] = useState([]);
  const [dashboardCount, setDashboardCounts] = useState({});
  const [jobProviderPerformance, setJobProviderPerformance] = useState([]);
  const [userGrowthData, setUserGrowthData] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [totalCount, setTotalCount] = useState(0);

  // Add a check to ensure value is a string before calling replace
  const checkValue = (value) => {
    if (typeof value !== 'string') {
      return "Zero"; // or any default value you prefer
    }
    // Remove % and convert to number
    const num = parseFloat(value.replace("%", ""));

    if (Number(num) > 0) {
      return "success.main";
    } else if (Number(num) < 0) {
      return "error.main";
    } else {
      return "";
    }
  };



  const statItems = [
    {
      title: "Total Users",
      value: dashboardCount.total_users,
      change: dashboardCount.user_count_change,
      changeColor: checkValue(dashboardCount.user_count_change),
      icon: dashboard.totalUsers,
    },
    {
      title: "Active Jobs",
      value: dashboardCount.active_jobs,
      change: dashboardCount.job_count_change,
      changeColor: checkValue(dashboardCount.job_count_change),
      icon: dashboard.activeJobs,
    },
    {
      title: "Monthly Revenue",
      value: dashboardCount.monthly_revenue,
      change: dashboardCount.monthly_revenue_change,
      changeColor: checkValue(dashboardCount.monthly_revenue_change),
      icon: dashboard.monthlyRevenue,
    },
    {
      title: "System Health",
      value: dashboardCount.system_health,
      // change: dashboardCount.system_health_change,
      // changeColor: checkValue(dashboardCount.system_health_change),
      icon: dashboard.systemHealth,
    },
  ];

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await jobProviderList(page, pageSize);
        setApiData(response.data.results.payload);
        setTotalCount(response.data.count);
      } catch (error) {
        console.error("Error fetching API data:", error);
      }
    };
    fetchData();
  }, [page, pageSize]); // The empty array ensures this effect runs once after the initial render

  useEffect(() => {
    handleDashboardCounts();
    handleUserGrowthGraph();
    handleJobProviderPerformance();
  }, []); // The empty array ensures this effect runs once after the initial render

  const handleDashboardCounts = async () => {
    try {
      const response = await dashboardCounts();
      setDashboardCounts(response.data.payload);
    } catch (error) {
      console.error("Error fetching API data:", error);
    }
  };
  const handleJobProviderPerformance = async () => {
    try {
      const response = await getJobProviderPerformance();
      setJobProviderPerformance(response?.data?.payload);
      console.log(response?.data?.payload)
    } catch (error) {
      console.error("Error fetching API data:", error);
      // toast.error("Error fetching job provider performance data");
    }
  };

  const handleUserGrowthGraph = async () => {
    try {
      const response = await userGrowthGraph();
      setUserGrowthData(response.data.payload);
    } catch (error) {
      console.error("Error fetching API data:", error);
    }
  };

  const userGrowthOptions = {
    chart: {
      type: "line",
      height: 300,
    },
    xAxis: {
      categories: userGrowthData.map((item) => item.month),
    },
    yAxis: {
      title: { text: null },
      gridLineDashStyle: "Dash",
    },
    legend: { enabled: false },
    credits: { enabled: false },
    title: { text: null },
    series: [
      {
        name: "Users",
        data: userGrowthData.map((item) => item.count),
        color: "#7cb5ec",
      },
    ],
  };

  // API Performance Chart Options
  const apiPerformanceOptions = {
    chart: {
      type: "line",
      height: 300,
    },
    credits: { enabled: false },
    title: { text: null },
    // Use the `day` from the first item to generate categories
    xAxis: {
      categories: jobProviderPerformance.length > 0 ? jobProviderPerformance.map(item => item.day) : [],
    },
    yAxis: {
      title: { text: null },
      gridLineDashStyle: "Dash",
    },
    legend: {
      layout: "horizontal",
      align: "center",
      verticalAlign: "bottom",
    },
    // Map over the providers of the first day to create series
    series: jobProviderPerformance.length > 0 ? jobProviderPerformance[0].providers.map(provider => ({
      name: provider.name,
      // Map over the main jobProviderPerformance array to get the performance for each provider for each day
      data: jobProviderPerformance.map(dayData => {
        const providerData = dayData.providers.find(p => p.name === provider.name);
        return providerData ? providerData.performance : 0;
      })
    })) : [],
  };

  const handleActionClick = async (id, isActive) => {
    try {
      const newIsActive = !isActive;
      await jobProviderUpdate({
        id: id,
        is_active: newIsActive,
      });
      // Update local state after successful API call
      setApiData((prevData) =>
        prevData.map((item) =>
          item.id === id
            ? {
                ...item,
                is_active: newIsActive,
                status: newIsActive ? "Active" : "Inactive",
              }
            : item
        )
      );
    } catch (error) {
      console.error("Error updating job provider status:", error);
    }
  };
  const navigate = useNavigate();
  const handleExportReport = async () => {
    try{
      const response = await adminDashboardReportExport();
      downloadBinaryFile(response, 'Admin Dashboard Report.xlsx');
    } catch (error) {
      console.error("Error fetching API data:", error);
      toast.error("Error fetching report export data",{toastId:"exportReportError"});
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#eaf3fc",
        minHeight: "94vh",
        padding: { xs: "14px", md: "36px" },
      }}
    >
      <Box
        sx={{
          background: "#ffffff",
          padding: "30px",
          border: "1px solid #1570EF",
          borderRadius: "8px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontSize: { xs: "24px", md: "36px", sm: "32px" },
              fontWeight: 600,
            }}
          >
            Dashboard
          </Typography>
          <Button variant="contained" className="export-report-btn" onClick={() => handleExportReport()}>
            Export Report
          </Button>
        </Box>

        <Box
          sx={{
            pt: 3,
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            width: "100%",
          }}
        >
          {statItems.map((item, index) => (
            <Box
              key={index}
              sx={{
                flexGrow: 1,
                flexBasis: "100%", // full width on mobile
                maxWidth: "100%",
                "@media (min-width:600px)": {
                  flexBasis: "calc(50% - 16px)", // 2 equal cards per row
                  maxWidth: "calc(50% - 16px)",
                },
                "@media (min-width:900px)": {
                  flexBasis: "calc(33.33% - 16px)", // 3 equal cards per row
                  maxWidth: "calc(33.33% - 16px)",
                },
                "@media (min-width:1200px)": {
                  flexBasis: "calc(25% - 16px)", // 4 equal cards per row
                  maxWidth: "calc(25% - 16px)",
                },
              }}
            >
              <StyledCard sx={{ height: "100%" }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <Box sx={{ width: "100%" }}>
                    <Typography
                      variant="subtitle1"
                      color="#333333"
                      gutterBottom
                    >
                      {item.title}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="h4"
                        component="div"
                        fontWeight="bold"
                      >
                        {item.value}
                      </Typography>
                      <img
                        src={item.icon}
                        alt={item.title}
                        style={{ objectFit: "contain" }}
                      />
                    </Box>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: item.changeColor }}>
                    {item.change}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="span"
                    >
                      {" "}
                      From Last Month
                    </Typography>
                  </Typography>
                </Box>
              </StyledCard>
            </Box>
          ))}
        </Box>

        <Box sx={{ flexGrow: 1, pt: 5 }}>
          <Grid container spacing={isMobile ? 2 : 4}>
            <Grid size={{ xs: 12, md: 6, sm: 12 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontWeight: 500,
                  textAlign: { xs: "center", md: "left" },
                }}
              >
                User Growth
              </Typography>
              <HighchartsReact
                highcharts={Highcharts}
                options={userGrowthOptions}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6, sm: 12 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontWeight: 500,
                  textAlign: { xs: "center", md: "left" },
                }}
              >
                API Performance
              </Typography>
              <HighchartsReact
                highcharts={Highcharts}
                options={apiPerformanceOptions}
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ pt: 3 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              mb: 2,
              fontSize: "18px",
              color: "#333333",
              textAlign: { xs: "center", md: "left" },
            }}
          >
            API Integrations
          </Typography>

          {/* Scrollable Table Section */}
          <Box
            sx={{
              overflowY: "auto",
              flex: 1,
            }}
          >
            <Table
              aria-label="upcoming interviews table"
              className="api-integration-table"
            >
              <TableHead>
                <TableRow>
                  <TableCell>NAME</TableCell>
                  <TableCell>PROVIDER</TableCell>
                  <TableCell>STATUS</TableCell>
                  <TableCell>LAST SYNC</TableCell>
                  <TableCell>ERROR RATE</TableCell>
                  <TableCell>ACTIONS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {apiData?.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.provider}</TableCell>
                    <TableCell>
                      <span
                        style={{
                          background:
                            row.status === "Active" ? "#e6f9f0" : "#fde7e7",
                          color:
                            row.status === "Active" ? "#1a7f37" : "#d32f2f",
                          padding: "4px 8px",
                          borderRadius: "6px",
                        }}
                      >
                        {row.status}
                      </span>
                    </TableCell>
                    <TableCell>{row.last_sync}</TableCell>
                    <TableCell
                      style={{
                        color: row.error_rate === "-" ? "green" : "red",
                      }}
                    >
                      {row.error_rate}
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          flexWrap: { xs: "wrap", md: "unset" },
                          fontSize: { xs: "11px", md: "15px" },
                          justifyContent: { xs: "center", md: "unset" },
                        }}
                      >
                        <button
                          style={{
                            border: "1px solid #333333",
                            borderRadius: "6px",
                            background: "#FFFFF",
                            padding: "4px 8px",
                            color: "#333333",
                            fontSize: "14px",
                          }}
                          onClick={() => navigate("/dashboard/api-setting")}
                        >
                          View Logs
                        </button>
                        <button
                          onClick={() =>
                            handleActionClick(row.id, row.is_active)
                          }
                          style={{
                            background: row.is_active ? "orange" : "#22C55E",
                            color: "#fff",
                            border: "none",
                            padding: "4px 8px",
                            borderRadius: "6px",
                            fontSize: "14px",
                          }}
                        >
                          {row.is_active ? "Pause" : "Active"}
                        </button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <Box
            sx={{
              mt: 2,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography variant="body2">
              Showing {(page - 1) * pageSize + 1} To{" "}
              {Math.min(page * pageSize, totalCount)} Of {totalCount} Results
            </Typography>
            <Pagination
              count={Math.ceil(totalCount / pageSize)}
              page={page}
              onChange={handlePageChange}
              shape="rounded"
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Home;